import { useEffect, useRef, useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { ArrowLeft, LoaderCircle, Globe, MapPin } from "lucide-react";
import { Logo } from "../components/landing/Logo";
import { useAuth, formatApiError } from "../contexts/AuthContext";
import { useMasters, PillSelect, SearchSelect, Chips, YesNo, LocationCascade, FunziSuggestions } from "../components/form/engine";
import { t, SUPPORTED_LANGS } from "../i18n";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ROLES = [
  { id: "student", emoji: "🎓", label: "Student", tag: "Learn, earn & get hired" },
  { id: "customer", emoji: "🛍️", label: "Customer", tag: "Discover everything" },
  { id: "chef", emoji: "👨‍🍳", label: "Student Chef", tag: "Sell your food on campus" },
  { id: "cloud_kitchen", emoji: "🥘", label: "Cloud Kitchen", tag: "Run a delivery kitchen" },
  { id: "farmer", emoji: "🌾", label: "Farmer", tag: "Sell directly to buyers" },
  { id: "vendor", emoji: "🏪", label: "Vendor", tag: "Grow your business" },
  { id: "creator", emoji: "🎤", label: "Creator", tag: "Earn from your content" },
  { id: "mentor", emoji: "🧑‍🏫", label: "Mentor", tag: "Teach & guide students" },
  { id: "recruiter", emoji: "🔎", label: "Recruiter", tag: "Find & place candidates" },
  { id: "hr", emoji: "🏢", label: "Corporate HR", tag: "Hire campus talent" },
  { id: "travel_partner", emoji: "✈️", label: "Travel Partner", tag: "Sell trips & packages" },
  { id: "cafe_owner", emoji: "☕", label: "Cafe Owner", tag: "Build your community" },
  { id: "rider", emoji: "🛵", label: "Delivery Rider", tag: "Deliver & earn" },
  { id: "school_admin", emoji: "🏫", label: "School Admin", tag: "Run your institution" },
  { id: "doctor", emoji: "🩺", label: "Doctor", tag: "Nutrition consults & diet plans" },
  { id: "nutritionist", emoji: "🥗", label: "Nutritionist", tag: "Custom diet programs" },
];

// Declarative dynamic forms — 80% select/chips/search, 15% auto-filled (postal), 5% free text
const FIELDS = {
  student: [
    { key: "college", label: "College", type: "search", options: "colleges" },
    { key: "course", label: "Course", type: "pills", options: "courses" },
    { key: "branch", label: "Branch", type: "pills", options: "branches" },
    { key: "year", label: "Year", type: "pills", options: "years" },
    { key: "skills", label: "Skills", type: "chips", options: "skills" },
    { key: "interests", label: "Interests", type: "chips", options: "interests" },
    { key: "location", label: "Location", type: "location" },
  ],
  customer: [
    { key: "mobile", label: "Mobile", type: "text", ph: "e.g. 98765 43210" },
    { key: "address", label: "Address", type: "text", ph: "Hostel / flat / area" },
    { key: "interests", label: "Interests", type: "chips", options: "interests" },
    { key: "location", label: "Location", type: "location" },
  ],
  chef: [
    { key: "kitchen_name", label: "Kitchen Name", type: "text", ph: "e.g. Priya's Kitchen" },
    { key: "kitchen_type", label: "Kitchen Type", type: "pills", options: "kitchen_types" },
    { key: "cuisine", label: "Cuisine Type", type: "chips", options: "cuisines" },
    { key: "experience", label: "Experience", type: "pills", options: "experience" },
    { key: "service_radius", label: "Service Radius", type: "pills", options: "service_radius" },
    { key: "fssai", label: "FSSAI Number", type: "text", ph: "14-digit FSSAI license" },
    { key: "bank_account", label: "Bank Account", type: "text", ph: "UPI ID or account for payouts" },
    { key: "location", label: "Location", type: "location" },
  ],
  cloud_kitchen: [
    { key: "kitchen_name", label: "Kitchen Name", type: "text", ph: "e.g. Midnight Fuel Co" },
    { key: "cuisine", label: "Cuisine Type", type: "chips", options: "cuisines" },
    { key: "capacity", label: "Capacity", type: "pills", options: "capacities" },
    { key: "fssai", label: "FSSAI Number", type: "text", ph: "14-digit FSSAI license" },
    { key: "location", label: "Location", type: "location" },
  ],
  farmer: [
    { key: "farm_name", label: "Farm Name", type: "text", ph: "e.g. Green Valley Farms" },
    { key: "farm_type", label: "Farm Type", type: "pills", options: "farm_types" },
    { key: "products", label: "Products", type: "chips", options: "farm_products" },
    { key: "capacity", label: "Production Capacity", type: "text", ph: "e.g. 2 tonnes / month" },
    { key: "location", label: "Location", type: "location" },
  ],
  vendor: [
    { key: "store_name", label: "Store Name", type: "text", ph: "e.g. Campus Threads" },
    { key: "category", label: "Store Category", type: "pills", options: "store_categories" },
    { key: "brand_type", label: "Brand Type", type: "pills", options: "brand_types" },
    { key: "gst", label: "GST / Tax Number", type: "text", ph: "Tax ID (optional)" },
    { key: "warehouse", label: "Warehouse", type: "text", ph: "Storage / fulfilment location" },
    { key: "bank_details", label: "Bank Details", type: "text", ph: "Account for payouts" },
    { key: "location", label: "Location", type: "location" },
  ],
  creator: [
    { key: "category", label: "Creator Category", type: "pills", options: "creator_categories" },
    { key: "specialization", label: "Specialization", type: "chips", options: "creator_specializations" },
    { key: "social_accounts", label: "Social Accounts", type: "text", ph: "e.g. @you on Insta / YouTube" },
    { key: "location", label: "Location", type: "location" },
  ],
  mentor: [
    { key: "specialization", label: "Specialization", type: "pills", options: "mentor_specializations" },
    { key: "subjects", label: "Subjects", type: "chips", options: "mentor_subjects" },
    { key: "experience", label: "Experience", type: "pills", options: "experience" },
  ],
  recruiter: [
    { key: "company", label: "Company", type: "text", ph: "e.g. Campus Placements Co" },
    { key: "hiring_type", label: "Recruitment Type", type: "chips", options: "recruitment_types" },
    { key: "industry", label: "Industry", type: "pills", options: "industries" },
    { key: "location", label: "Location", type: "location" },
  ],
  hr: [
    { key: "company", label: "Company", type: "text", ph: "e.g. Zeta" },
    { key: "designation", label: "Designation", type: "text", ph: "e.g. HR Manager" },
    { key: "industry", label: "Industry", type: "pills", options: "industries" },
    { key: "company_size", label: "Company Size", type: "pills", options: "company_sizes" },
  ],
  travel_partner: [
    { key: "agency_name", label: "Agency Name", type: "text", ph: "e.g. WanderCampus" },
    { key: "service_type", label: "Service Type", type: "chips", options: "travel_services" },
    { key: "location", label: "Base Location", type: "location" },
  ],
  cafe_owner: [
    { key: "cafe_name", label: "Cafe Name", type: "text", ph: "e.g. Brew & Byte" },
    { key: "property_type", label: "Property Type", type: "pills", options: "property_types" },
    { key: "kitchen_available", label: "Kitchen Availability", type: "yesno" },
    { key: "rooms", label: "Rooms", type: "pills", options: "room_counts" },
    { key: "location", label: "Location", type: "location" },
  ],
  rider: [
    { key: "vehicle_type", label: "Vehicle Type", type: "pills", options: "vehicles" },
    { key: "license", label: "License Number", type: "text", ph: "Driving license (if motorised)" },
    { key: "location", label: "Location", type: "location" },
  ],
  school_admin: [
    { key: "school_name", label: "School / Institution", type: "text", ph: "e.g. Springdale Public School" },
    { key: "designation", label: "Designation", type: "pills", options: "designations" },
    { key: "location", label: "Location", type: "location" },
  ],
  doctor: [
    { key: "specialization", label: "Specialization", type: "pills", options: "med_specializations" },
    { key: "license", label: "Medical License No.", type: "text", ph: "e.g. MCI-88412" },
    { key: "location", label: "Location", type: "location" },
  ],
  nutritionist: [
    { key: "specialization", label: "Specialization", type: "pills", options: "med_specializations" },
    { key: "experience", label: "Experience", type: "pills", options: "experience" },
    { key: "location", label: "Location", type: "location" },
  ],
};

// Browser-based country detection — timezone first, language as fallback.
const TZ_COUNTRY = {
  "Asia/Kolkata": "India", "Asia/Calcutta": "India", "Asia/Dubai": "UAE",
  "America/New_York": "United States", "America/Chicago": "United States", "America/Denver": "United States",
  "America/Los_Angeles": "United States", "America/Phoenix": "United States", "America/Anchorage": "United States",
  "Pacific/Honolulu": "United States", "Europe/London": "United Kingdom",
  "America/Toronto": "Canada", "America/Vancouver": "Canada", "America/Winnipeg": "Canada", "America/Halifax": "Canada",
  "Australia/Sydney": "Australia", "Australia/Melbourne": "Australia", "Australia/Brisbane": "Australia", "Australia/Perth": "Australia",
  "Pacific/Auckland": "New Zealand", "Asia/Singapore": "Singapore",
  "Europe/Berlin": "Germany", "Europe/Munich": "Germany", "Europe/Paris": "France",
  "Asia/Tokyo": "Japan", "Asia/Seoul": "South Korea",
};
const LOCALE_COUNTRY = {
  "en-US": "United States", "en-GB": "United Kingdom", "en-CA": "Canada", "en-AU": "Australia",
  "en-NZ": "New Zealand", "en-SG": "Singapore", "en-IN": "India", "hi": "India", "hi-IN": "India",
  "ar": "UAE", "ar-AE": "UAE", "fr": "France", "fr-FR": "France", "fr-CA": "Canada",
  "de": "Germany", "de-DE": "Germany", "ja": "Japan", "ko": "South Korea",
  // bare language fallbacks for sandboxed/minimal browser locales (en, en-US@posix, …)
  "en": "United States", "es": "Rest of World", "zh": "Singapore",
};

// Worldwide address search (Google Places) — only renders when the backend has a key.
const AddressSearch = ({ label, onPick }) => {
  const [q, setQ] = useState("");
  const [opts, setOpts] = useState([]);
  const [picked, setPicked] = useState("");
  const timer = useRef(null);

  const search = (text) => {
    setQ(text);
    setPicked("");
    clearTimeout(timer.current);
    if (text.trim().length < 3) return setOpts([]);
    timer.current = setTimeout(async () => {
      try {
        const { data } = await axios.get(`${API}/geo/search`, { params: { q: text.trim() } });
        setOpts(data.suggestions || []);
      } catch {
        setOpts([]);
      }
    }, 400);
  };

  const choose = async (s) => {
    setOpts([]);
    setQ(s.description);
    try {
      const { data } = await axios.get(`${API}/geo/details/${s.place_id}`);
      setPicked(data.formatted || s.description);
      onPick(data);
    } catch {
      onPick({ locality: s.description, formatted: s.description });
    }
  };

  return (
    <div className="relative sm:col-span-2" data-testid="address-search">
      <p className="mb-1.5 font-jbmono text-[10px] uppercase tracking-widest text-slate-500">{label}</p>
      <div className="relative">
        <MapPin className="absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-500 rtl:left-auto rtl:right-4" />
        <input
          data-testid="address-search-input"
          value={q}
          onChange={(e) => search(e.target.value)}
          placeholder="e.g. Shibuya, Austin TX, Business Bay…"
          className="w-full rounded-2xl border border-white/10 bg-[#09090B]/80 py-3 pl-11 pr-4 text-sm text-white placeholder:text-slate-600 outline-none focus:border-[#06B6D4]/60 rtl:pl-4 rtl:pr-11"
        />
      </div>
      {opts.length > 0 && (
        <div className="absolute z-30 mt-2 w-full rounded-2xl border border-white/10 bg-[#09090B] p-1.5 shadow-2xl">
          {opts.map((s) => (
            <button
              type="button"
              key={s.place_id || s.description}
              data-testid={`address-option-${(s.place_id || "").slice(-6)}`}
              onClick={() => choose(s)}
              className="block w-full rounded-xl px-3 py-2 text-left text-xs text-slate-300 transition-colors hover:bg-white/5"
            >
              {s.description}
            </button>
          ))}
        </div>
      )}
      {picked && <p className="mt-1.5 font-jbmono text-[10px] text-[#22C55E]" data-testid="address-picked">✓ {picked}</p>}
    </div>
  );
};

export default function Onboarding() {
  const { user, checked, setUser } = useAuth();
  const navigate = useNavigate();
  const masters = useMasters();
  const [step, setStep] = useState("geo"); // geo → role → form
  const [country, setCountry] = useState("");
  const [language, setLanguage] = useState("");
  const [detected, setDetected] = useState("");
  const [role, setRole] = useState(null);
  const [form, setForm] = useState({});
  const [busy, setBusy] = useState(false);
  const [resumeText, setResumeText] = useState("");
  const [autofilling, setAutofilling] = useState(false);
  const [autofillOpen, setAutofillOpen] = useState(false);
  const [geoEnabled, setGeoEnabled] = useState(false);

  const countries = masters.locations || [];
  const countryNode = countries.find((c) => c.country === country);
  const languages = countryNode?.languages || [];

  // UI language: the one being picked, else the browser's language when supported
  const browserLang = (navigator.language || "en").slice(0, 2);
  const uiLang = language || (SUPPORTED_LANGS.includes(browserLang) ? browserLang : "en");
  const T = (k) => t(uiLang, k);

  // Auto-detect country from the browser on first visit
  useEffect(() => {
    if (country || !countries.length) return;
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone || "";
    let guess = TZ_COUNTRY[tz];
    if (!guess) {
      const loc = navigator.language || "";
      guess = LOCALE_COUNTRY[loc] || LOCALE_COUNTRY[loc.split("-")[0]];
    }
    const node = guess && countries.find((c) => c.country === guess);
    if (node) {
      setCountry(node.country);
      setDetected(node.country);
      const def = (node.languages || []).find((l) => l.default) || (node.languages || [])[0];
      if (def) setLanguage(def.code);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [countries]);

  useEffect(() => {
    axios.get(`${API}/geo/config`).then((r) => setGeoEnabled(!!r.data.enabled)).catch(() => {});
  }, []);

  if (!checked) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#09090B]">
        <LoaderCircle className="h-8 w-8 animate-spin text-[#06B6D4]" />
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;
  if (user.onboarded && user.role !== "user") return <Navigate to="/app" replace />;

  const fieldMap = (masters.field_map || {})[role] || [];
  const erpFor = (key) => fieldMap.find((f) => f.key === key);

  const submit = async (pickedRole, profile, geo) => {
    setBusy(true);
    try {
      const { data } = await axios.post(
        `${API}/auth/onboarding`,
        { role: pickedRole, profile, country: geo?.country || country, language: geo?.language || language },
        { headers: { Authorization: `Bearer ${localStorage.getItem("funz_token")}` }, withCredentials: true }
      );
      setUser(data);
      toast.success(`Welcome aboard, ${ROLES.find((r) => r.id === pickedRole)?.label}! 🎉`);
      navigate("/app");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const serialize = () => {
    const profile = {};
    (FIELDS[role] || []).forEach((f) => {
      const v = form[f.key];
      if (f.type === "chips") {
        if (Array.isArray(v) && v.length) profile[f.key] = v.join(", ");
      } else if (f.type === "location") {
        if (v?.locality) {
          profile[f.key] = [v.locality, v.l3, v.l2, v.l1, country].filter(Boolean).join(", ");
          if (v.pincode) profile.pincode = v.pincode;
        }
      } else if (typeof v === "string" && v.trim()) {
        profile[f.key] = v.trim();
      }
    });
    return profile;
  };

  const validate = () => {
    const profile = serialize();
    const missing = fieldMap.filter((f) => f.required && !profile[f.key]).map((f) => f.key);
    if (missing.length) {
      toast.error(`Required for Zoho/SAP mapping: ${missing.join(", ")}`);
      return false;
    }
    return true;
  };

  const fields = role ? FIELDS[role] : [];
  const hasInterests = fields.some((f) => f.key === "interests");
  const hasLocation = fields.some((f) => f.type === "location");

  const autofill = async () => {
    setAutofilling(true);
    try {
      const { data } = await axios.post(
        `${API}/onboarding/autofill`,
        { role, text: resumeText },
        { headers: { Authorization: `Bearer ${localStorage.getItem("funz_token")}` }, withCredentials: true }
      );
      const profile = data.profile || {};
      const next = { ...form };
      fields.forEach((f) => {
        const v = profile[f.key];
        if (!v) return;
        if (f.type === "chips") next[f.key] = v.split(",").map((s) => s.trim()).filter(Boolean);
        else if (f.type !== "location") next[f.key] = v;
      });
      setForm(next);
      setAutofillOpen(false);
      toast.success(`FUNZI filled ${Object.keys(profile).length} fields ✨ — review before submitting`);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setAutofilling(false);
    }
  };

  const renderField = (f) => {
    const erp = erpFor(f.key);
    const common = { label: f.label, testid: `field-${f.key}`, erp };
    switch (f.type) {
      case "search":
        return <SearchSelect key={f.key} {...common} options={masters[f.options] || []} value={form[f.key] || ""} onChange={(v) => setForm({ ...form, [f.key]: v })} />;
      case "pills":
        return <PillSelect key={f.key} {...common} options={masters[f.options] || []} value={form[f.key] || ""} onChange={(v) => setForm({ ...form, [f.key]: v })} />;
      case "chips":
        return <Chips key={f.key} {...common} options={masters[f.options] || []} values={form[f.key] || []} onChange={(v) => setForm({ ...form, [f.key]: v })} />;
      case "yesno":
        return <YesNo key={f.key} {...common} value={form[f.key] || ""} onChange={(v) => setForm({ ...form, [f.key]: v })} />;
      case "location":
        return <LocationCascade key={f.key} masters={masters} country={country} value={form[f.key] || {}} onChange={(v) => setForm({ ...form, [f.key]: v })} />;
      default:
        return (
          <div key={f.key}>
            <p className="mb-1.5 font-jbmono text-[10px] uppercase tracking-widest text-slate-500">
              {f.label}
              {erp?.required && <span className="ml-1 text-[#F97316]">*</span>}
            </p>
            <input
              data-testid={`field-${f.key}`}
              value={form[f.key] || ""}
              onChange={(e) => setForm({ ...form, [f.key]: e.target.value })}
              placeholder={f.ph}
              className="w-full rounded-2xl border border-white/10 bg-[#09090B]/80 px-4 py-3 text-sm text-white placeholder:text-slate-600 outline-none focus:border-[#7C3AED]/60"
            />
          </div>
        );
    }
  };

  const STEP_LABELS = { geo: T("ob_step1"), role: T("ob_step2"), form: `${T("ob_step3")} · ${ROLES.find((r) => r.id === role)?.label || ""}` };

  return (
    <div data-testid="onboarding-page" dir={uiLang === "ar" ? "rtl" : "ltr"} className="min-h-screen bg-[#09090B] px-4 py-10 text-white sm:px-6">
      <div className="mx-auto max-w-3xl">
        <div className="flex items-center gap-3">
          <Logo className="h-10 w-10" />
          <div>
            <p className="font-display text-xl font-extrabold">{T("ob_title")}</p>
            <p className="font-jbmono text-[10px] uppercase tracking-[0.25em] text-[#06B6D4]">{STEP_LABELS[step]}</p>
          </div>
        </div>

        {step === "geo" && (
          <div className="mt-8 rounded-3xl border border-white/10 bg-white/[0.04] p-6 backdrop-blur-xl sm:p-8" data-testid="geo-step">
            <p className="flex items-center gap-2 font-display text-lg font-bold">
              <Globe className="h-5 w-5 text-[#06B6D4]" /> {T("ob_where")}
            </p>
            <p className="mt-1 text-xs text-slate-500">
              {T("ob_where_sub")}
            </p>
            <div className="mt-6 space-y-5">
              <SearchSelect
                label={T("ob_country")}
                options={countries.map((c) => `${c.flag} ${c.country}`)}
                value={country ? `${countryNode?.flag} ${country}` : ""}
                onChange={(v) => {
                  setCountry(v.replace(/^[^\s]+\s/, ""));
                  setDetected("");
                  setLanguage("");
                }}
                placeholder="🔍 Search country…"
                testid="geo-country"
              />
              {detected && country === detected && (
                <p className="flex items-center gap-1.5 font-jbmono text-[10px] text-[#22C55E]" data-testid="geo-detected">
                  <MapPin className="h-3 w-3" /> {countryNode?.flag} {detected} · {T("ob_detected")}
                </p>
              )}
              {country && languages.length > 0 && (
                <PillSelect
                  label={T("ob_language")}
                  options={languages.map((l) => l.name)}
                  value={languages.find((l) => l.code === language)?.name || ""}
                  onChange={(v) => setLanguage(languages.find((l) => l.name === v)?.code || "")}
                  testid="geo-language"
                />
              )}
              {country && (
                <p className="font-jbmono text-[10px] uppercase tracking-widest text-slate-500" data-testid="geo-meta">
                  {countryNode?.flag} {country} · {countryNode?.currency} {countryNode?.symbol} · {countryNode?.timezone}
                </p>
              )}
              <button
                data-testid="geo-continue"
                onClick={() => setStep("role")}
                disabled={!country}
                className="w-full rounded-full bg-gradient-to-r from-[#7C3AED] to-[#06B6D4] py-4 font-display text-sm font-bold uppercase tracking-wider text-white disabled:opacity-50"
              >
                {T("ob_continue")}
              </button>
              <button
                data-testid="onboarding-skip"
                onClick={() => submit("customer", {}, { country: "India", language: "en" })}
                disabled={busy}
                className="text-xs text-slate-500 underline underline-offset-4 hover:text-slate-300"
              >
                {T("ob_skip")}
              </button>
            </div>
          </div>
        )}

        {step === "role" && (
          <>
            <button data-testid="role-back" onClick={() => setStep("geo")} className="mt-6 inline-flex items-center gap-1.5 text-xs text-slate-400 hover:text-white">
              <ArrowLeft className="h-3.5 w-3.5 rtl:rotate-180" /> {T("ob_change_country")}
            </button>
            <div className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-3" data-testid="role-picker">
              {ROLES.map((r) => (
                <button
                  key={r.id}
                  data-testid={`role-${r.id}`}
                  onClick={() => {
                    setRole(r.id);
                    setStep("form");
                  }}
                  className="group rounded-2xl border border-white/10 bg-white/[0.04] p-5 text-left backdrop-blur-xl transition-all hover:-translate-y-1 hover:border-[#7C3AED]/60 rtl:text-right"
                >
                  <span className="inline-block text-3xl transition-transform duration-300 group-hover:scale-125">{r.emoji}</span>
                  <p className="mt-2.5 text-sm font-bold">{r.label}</p>
                  <p className="mt-0.5 font-jbmono text-[10px] text-slate-500">{r.tag}</p>
                </button>
              ))}
            </div>
          </>
        )}

        {step === "form" && role && (
          <div className="mt-8 rounded-3xl border border-white/10 bg-white/[0.04] p-6 backdrop-blur-xl sm:p-8" data-testid="onboarding-form">
            <button
              data-testid="onboarding-back"
              onClick={() => setStep("role")}
              className="mb-5 inline-flex items-center gap-1.5 text-xs text-slate-400 hover:text-white"
            >
              <ArrowLeft className="h-3.5 w-3.5 rtl:rotate-180" /> {T("ob_change_role")}
            </button>
            <p className="font-display text-lg font-bold">
              {ROLES.find((r) => r.id === role)?.emoji} {ROLES.find((r) => r.id === role)?.label} {T("ob_registration")}
            </p>
            <p className="mt-1 font-jbmono text-[10px] uppercase tracking-widest text-slate-600">
              {countryNode?.flag} {country} · <span className="text-[#F97316]">*</span> {T("ob_mapped")}
            </p>

            <div className="mt-4">
              {!autofillOpen ? (
                <button
                  data-testid="autofill-open"
                  onClick={() => setAutofillOpen(true)}
                  className="flex w-full items-center justify-center gap-2 rounded-2xl border border-dashed border-[#06B6D4]/40 bg-[#06B6D4]/[0.05] py-3.5 font-display text-xs font-bold uppercase tracking-wider text-[#06B6D4] transition-colors hover:bg-[#06B6D4]/10"
                >
                  {T("ob_autofill")}
                </button>
              ) : (
                <div className="rounded-2xl border border-[#06B6D4]/30 bg-[#06B6D4]/[0.05] p-4" data-testid="autofill-box">
                  <textarea
                    data-testid="autofill-text"
                    value={resumeText}
                    onChange={(e) => setResumeText(e.target.value)}
                    rows={4}
                    placeholder={T("ob_autofill_ph")}
                    className="w-full rounded-xl border border-white/10 bg-[#09090B]/80 px-4 py-3 text-sm text-white placeholder:text-slate-600 outline-none focus:border-[#06B6D4]/60"
                  />
                  <div className="mt-3 flex gap-2">
                    <button
                      data-testid="autofill-run"
                      onClick={autofill}
                      disabled={autofilling || resumeText.trim().length < 10}
                      className="flex items-center gap-2 rounded-full bg-gradient-to-r from-[#7C3AED] to-[#06B6D4] px-5 py-2.5 font-display text-xs font-bold uppercase tracking-wider text-white disabled:opacity-50"
                    >
                      {autofilling ? <LoaderCircle className="h-3.5 w-3.5 animate-spin" /> : T("ob_run")}
                    </button>
                    <button data-testid="autofill-cancel" onClick={() => setAutofillOpen(false)} className="rounded-full border border-white/15 px-4 py-2.5 text-xs text-slate-400 hover:text-white">
                      {T("cancel")}
                    </button>
                  </div>
                </div>
              )}
            </div>

            <div className="mt-5 grid grid-cols-1 gap-5 sm:grid-cols-2">
              {geoEnabled && hasLocation && (
                <AddressSearch
                  label={T("ob_address_search")}
                  onPick={(d) =>
                    setForm({
                      ...form,
                      location: { l1: d.state || "", l2: d.city || "", l3: d.area || "", locality: d.locality || d.formatted || "", pincode: d.pincode || "" },
                    })
                  }
                />
              )}
              {fields.map(renderField)}
              {hasInterests && (
                <FunziSuggestions
                  form={{ ...form, interests: (form.interests || []).join(" ") }}
                  masters={masters}
                  onPick={(s) => {
                    const cur = form.interests || [];
                    if (!cur.includes(s)) setForm({ ...form, interests: [...cur, s] });
                  }}
                />
              )}
            </div>
            <button
              data-testid="onboarding-submit"
              onClick={() => validate() && submit(role, serialize())}
              disabled={busy || !fieldMap.length}
              className="mt-6 flex w-full items-center justify-center gap-2 rounded-full bg-gradient-to-r from-[#7C3AED] to-[#06B6D4] py-4 font-display text-sm font-bold uppercase tracking-wider text-white disabled:opacity-60"
            >
              {busy ? <LoaderCircle className="h-4 w-4 animate-spin" /> : T("ob_submit")}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
